import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  activateSubscription,
  renewSubscription,
  expireSubscription,
  getSubscriptionById,
} from "../../../services/operations/subscriptionsAPI";
import { getSchools } from "../../../services/operations/schoolAPI";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";

/* ---------------- Manage Modal ---------------- */
const ManageModal = ({ selectedSchool, form, setForm, onSubmit, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black/40 flex justify-center items-center p-4 z-50">
      <motion.div
        initial={{ scale: 0.85, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white p-6 rounded-xl w-full max-w-md shadow-xl border border-gray-200"
      >
        <h2 className="text-xl font-bold mb-4">Manage Subscription</h2>

        <p className="text-gray-600 mb-4 text-sm">
          School:{" "}
          <span className="font-semibold">{selectedSchool?.schoolName}</span>
        </p>

        <label className="block text-gray-700 font-medium mb-1">Plan</label>
        <select
          className="w-full p-3 border rounded-lg mb-3 bg-gray-100"
          value={form.plan}
          onChange={(e) => setForm({ ...form, plan: e.target.value })}
        >
          <option value="premium">Premium</option>
        </select>

        <label className="block text-gray-700 font-medium mb-1">
          Expire Date
        </label>
        <input
          type="date"
          className="w-full p-3 border rounded-lg mb-3 bg-gray-100"
          value={form.expireDate}
          onChange={(e) => setForm({ ...form, expireDate: e.target.value })}
        />

        <label className="block text-gray-700 font-medium mb-1">
          API Limit per Day
        </label>
        <input
          type="number"
          min={0}
          className="w-full p-3 border rounded-lg mb-3 bg-gray-100"
          value={form.apiLimitPerDay || ""}
          onChange={(e) => setForm({ ...form, apiLimitPerDay: e.target.value })}
        />

        <div className="flex justify-end space-x-3 pt-3">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-300 hover:bg-gray-400 rounded-lg"
          >
            Cancel
          </button>

          <button
            onClick={onSubmit}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
          >
            Save
          </button>
        </div>
      </motion.div>
    </div>
  );
};

/* --------------- Expire Confirmation Modal --------------- */
const ExpireConfirmationModal = ({
  schoolName,
  onConfirm,
  onClose,
  loading,
}) => {
  return (
    <div className="fixed inset-0 bg-black/40 flex justify-center items-center p-4 z-50">
      <motion.div
        initial={{ scale: 0.85, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white p-6 rounded-xl w-full max-w-sm shadow-xl"
      >
        <h2 className="text-xl font-semibold text-red-600 mb-4">
          Confirm Expiry
        </h2>

        <p className="text-gray-700 text-sm mb-6">
          Are you sure you want to expire subscription for{" "}
          <span className="font-semibold">{schoolName}</span>?
        </p>

        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 bg-gray-300 hover:bg-gray-400 rounded-lg disabled:opacity-50"
          >
            Cancel
          </button>

          <button
            onClick={onConfirm}
            disabled={loading}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg disabled:opacity-50 flex items-center"
          >
            {loading && (
              <svg
                className="animate-spin h-4 w-4 mr-2 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v4l3-3-3-3v4a12 12 0 100 24v-4l-3 3 3 3v-4a8 8 0 01-8-8z"
                />
              </svg>
            )}
            Yes, Expire
          </button>
        </div>
      </motion.div>
    </div>
  );
};

/* ------------------ Main Component ------------------ */
const SubscriptionManagement = () => {
  const [loading, setLoading] = useState(true);
  const [subscriptions, setSubscriptions] = useState([]);
  const { user } = useSelector((state) => state.profile);

  const [modalOpen, setModalOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [expiring, setExpiring] = useState(false);
  const [schoolToExpire, setSchoolToExpire] = useState(null);

  const [selectedSchool, setSelectedSchool] = useState(null);
  const [form, setForm] = useState({
    plan: "premium",
    expireDate: "",
    apiLimitPerDay: "",
  });

  /* ---------------- Fetch schools and full subscription ---------------- */
  const loadSubscriptions = async () => {
    setLoading(true);
    try {
      const schoolList = await getSchools();
      if (!schoolList) {
        setSubscriptions([]);
        return setLoading(false);
      }

      const mapped = await Promise.all(
        schoolList.map(async (school) => {
          let subscriptionDetails = null;

          if (school.activeSubscriptionId) {
            subscriptionDetails = await getSubscriptionById(
              school.activeSubscriptionId
            );
          }

          return {
            _id: school._id,
            schoolName: school.name,
            email: school.email,
            subscriptionStatus: school.subscriptionStatus || "none",
            subscription: subscriptionDetails,
          };
        })
      );

      setSubscriptions(mapped);
    } catch (err) {
      console.error("Load subscriptions error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSubscriptions();
  }, []);

  /* ---------------- Open Manage Modal ---------------- */
  const openModal = (school) => {
    setSelectedSchool(school);

    setForm({
      plan: school.subscription?.plan || "premium",
      expireDate: school.subscription?.endDate
        ? new Date(school.subscription.endDate).toISOString().split("T")[0]
        : "",
      apiLimitPerDay: school.subscription?.apiLimitPerDay || "",
    });

    setModalOpen(true);
  };

  /* ---------------- Submit Activate / Renew ---------------- */
  const handleSubmit = async () => {
    if (!form.expireDate) return toast.error("Please select expire date");

    try {
      let response;

      if (selectedSchool.subscription?.isActive) {
        // Renew subscription - pass separate parameters
        response = await renewSubscription(
          selectedSchool._id,
          form.expireDate, 
          Number(form.apiLimitPerDay) || 0 
        );
      } else {
        // Activate new subscription
        response = await activateSubscription({
          schoolId: selectedSchool._id,
          plan: form.plan,
          startDate: new Date().toISOString(),
          endDate: form.expireDate,
          apiLimitPerDay: Number(form.apiLimitPerDay) || 0,
          activatedBy: user?._id,
        });
      }

      if (response) {
        setModalOpen(false);
        await loadSubscriptions();
      }
    } catch (err) {
      console.error("Manage submit error:", err);
      toast.error("Something went wrong while saving subscription");
    }
  };

  /* ---------------- Expire Subscription ---------------- */
  const handleOpenExpireConfirm = (school) => {
    setSchoolToExpire(school);
    setConfirmOpen(true);
  };

  const handleExpireConfirm = async () => {
    if (!schoolToExpire) return;

    setConfirmOpen(false);
    setExpiring(true);

    try {
      await expireSubscription(schoolToExpire._id);
    } catch (err) {
      console.error("Expire API error:", err);
    } finally {
      setExpiring(false);
      setSchoolToExpire(null);
      await loadSubscriptions();
    }
  };

  return (
    <div className="p-6 bg-white min-h-screen">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-gray-800">
          Subscription Management
        </h1>
      </div>

      <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
        {loading ? (
          <p className="text-center py-10 text-gray-500">Loading...</p>
        ) : (
          <table className="w-full text-left text-gray-700">
            <thead>
              <tr className="border-b text-gray-500">
                <th className="py-3">School</th>
                <th className="py-3">Email</th>
                <th className="py-3">Subscription</th>
                <th className="py-3 text-right">Actions</th>
              </tr>
            </thead>

            <tbody>
              {subscriptions.map((item, i) => (
                <motion.tr
                  key={item._id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.04 }}
                  className="border-b hover:bg-gray-50"
                >
                  <td className="py-3">{item.schoolName}</td>
                  <td className="py-3">{item.email}</td>

                  <td className="py-3 text-xs space-y-1">
                    {item.subscription ? (
                      <>
                        <div>Plan: {item.subscription.plan}</div>
                        <div>
                          Status:{" "}
                          {item.subscription.isActive ? "Active" : "Expired"}
                        </div>
                        <div>
                          Start:{" "}
                          {new Date(
                            item.subscription.startDate
                          ).toLocaleDateString()}
                        </div>
                        <div>
                          End:{" "}
                          {new Date(
                            item.subscription.endDate
                          ).toLocaleDateString()}
                        </div>
                        <div>
                          Activated By:{" "}
                          {item.subscription.activatedBy?.name ||
                            item.subscription.activatedBy ||
                            "-"}
                        </div>
                        <div>
                          API Limit/Day: {item.subscription.apiLimitPerDay}
                        </div>
                        <div>
                          API Calls Today: {item.subscription.apiCallsToday}
                        </div>
                        <div>
                          Usage Date:{" "}
                          {item.subscription.apiUsageDate
                            ? new Date(
                                item.subscription.apiUsageDate
                              ).toLocaleDateString()
                            : "-"}
                        </div>
                      </>
                    ) : (
                      <span className="text-gray-400">No Subscription</span>
                    )}
                  </td>

                  <td className="py-3 flex justify-end space-x-2">
                    <button
                      className="px-3 py-1 bg-blue-600 text-white hover:bg-blue-700 rounded-lg text-sm shadow"
                      onClick={() => openModal(item)}
                    >
                      Activate / Renew
                    </button>

                    {item.subscription && (
                      <button
                        className="px-3 py-1 bg-red-600 text-white hover:bg-red-700 rounded-lg text-sm shadow"
                        onClick={() => handleOpenExpireConfirm(item)}
                      >
                        Expire
                      </button>
                    )}
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Manage Modal */}
      {modalOpen && (
        <ManageModal
          selectedSchool={selectedSchool}
          form={form}
          setForm={setForm}
          onSubmit={handleSubmit}
          onClose={() => setModalOpen(false)}
        />
      )}

      {/* Expire Confirm Modal */}
      {confirmOpen && (
        <ExpireConfirmationModal
          schoolName={schoolToExpire?.schoolName || "this school"}
          onConfirm={handleExpireConfirm}
          onClose={() => setConfirmOpen(false)}
          loading={expiring}
        />
      )}
    </div>
  );
};

export default SubscriptionManagement;
